import React, { createContext, useCallback, useRef, useState } from "react";
import type { InputRef } from "antd";
import type { TextAreaRef } from "rc-textarea";
import { completeTour as completeTourApi } from "../../service/client";
import { useResponsive } from "../../hooks/useResponsive";

export interface TourRefs {
  ordersNavRef: React.RefObject<HTMLDivElement>;
  mobileOrdersMenuRef: React.RefObject<HTMLDivElement>;
  mobileMenuBtnRef: React.RefObject<HTMLDivElement>;
  menuNavRef: React.RefObject<HTMLButtonElement>;
  addCategoryBtnRef: React.RefObject<HTMLButtonElement>;
  categoryNameInputRef: React.RefObject<InputRef>;
  categoryModalOkBtnRef: React.RefObject<HTMLSpanElement>;
  addProductBtnRef: React.RefObject<HTMLButtonElement>;
  productNameInputRef: React.RefObject<InputRef>;
  productPriceInputRef: React.RefObject<InputRef>;
  productDescInputRef: React.RefObject<TextAreaRef>;
  createProductBtnRef: React.RefObject<HTMLButtonElement>;
  globeRef: React.RefObject<HTMLAnchorElement>;
}

export interface TourStepIndexes {
  WELCOME: number;
  HAMBURGER_1: number;
  ORDERS_NAV: number;
  MENU_NAV: number;
  ADD_CATEGORY_BTN: number;
  CATEGORY_MODAL_OPEN: number;
  ADD_PRODUCT_BTN: number;
  PRODUCT_MODAL_OPEN: number;
  HAMBURGER_2: number;
  GLOBE: number;
  END: number;
}

export interface TourContextType extends TourRefs {
  isTourOpen: boolean;
  tourStep: number;
  stepIndexes: TourStepIndexes;
  setIsTourOpen: (open: boolean) => void;
  setTourStep: (step: number) => void;
  startTour: () => void;
  advanceTourStep: (fromStep: number) => void;
  finishTour: () => void;
}

export const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourRefsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const { isMobile } = useResponsive();

  const stepIndexes: TourStepIndexes = {
    WELCOME: 0,
    HAMBURGER_1: isMobile ? 1 : -1,
    ORDERS_NAV: isMobile ? 2 : 1,
    MENU_NAV: isMobile ? 3 : 2,
    ADD_CATEGORY_BTN: isMobile ? 4 : 3,
    CATEGORY_MODAL_OPEN: isMobile ? 5 : 4,
    ADD_PRODUCT_BTN: isMobile ? 6 : 5,
    PRODUCT_MODAL_OPEN: isMobile ? 7 : 6,
    HAMBURGER_2: isMobile ? 8 : -1,
    GLOBE: isMobile ? 9 : 7,
    END: isMobile ? 10 : 8,
  };

  const ordersNavRef = useRef<HTMLDivElement>(null);
  const mobileOrdersMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuBtnRef = useRef<HTMLDivElement>(null);
  const menuNavRef = useRef<HTMLButtonElement>(null);
  const addCategoryBtnRef = useRef<HTMLButtonElement>(null);
  const categoryNameInputRef = useRef<InputRef>(null);
  const categoryModalOkBtnRef = useRef<HTMLSpanElement>(null);
  const addProductBtnRef = useRef<HTMLButtonElement>(null);
  const productNameInputRef = useRef<InputRef>(null);
  const productPriceInputRef = useRef<InputRef>(null);
  const productDescInputRef = useRef<TextAreaRef>(null);
  const createProductBtnRef = useRef<HTMLButtonElement>(null);
  const globeRef = useRef<HTMLAnchorElement>(null);

  const startTour = useCallback(() => {
    setTourStep(0);
    setIsTourOpen(true);
  }, []);

  const advanceTourStep = useCallback((fromStep: number) => {
    setTourStep((current) => {
      if (current === fromStep) return fromStep + 1;
      return current;
    });
  }, []);

  const finishTour = useCallback(() => {
    setIsTourOpen(false);
    completeTourApi().catch(() => { });
  }, []);

  return (
    <TourContext.Provider
      value={{
        isTourOpen,
        tourStep,
        stepIndexes,
        setIsTourOpen,
        setTourStep,
        startTour,
        advanceTourStep,
        finishTour,
        ordersNavRef,
        mobileOrdersMenuRef,
        mobileMenuBtnRef,
        menuNavRef,
        addCategoryBtnRef,
        categoryNameInputRef,
        categoryModalOkBtnRef,
        addProductBtnRef,
        productNameInputRef,
        productPriceInputRef,
        productDescInputRef,
        createProductBtnRef,
        globeRef,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};
